<script setup>
import { ref, computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const tools = computed({
  get: () => props.modelValue || [],
  set: val => emit('update:modelValue', val),
});

const isModalOpen = ref(false);
const editingIndex = ref(-1);
const toolForm = ref({
  nome: '',
  descricao: '',
  conteudo: '',
  ordem: 0,
});

const openAddTool = () => {
  editingIndex.value = -1;
  toolForm.value = {
    nome: '',
    descricao: '',
    conteudo: '',
    ordem: tools.value.length,
  };
  isModalOpen.value = true;
};

const openEditTool = (tool, index) => {
  editingIndex.value = index;
  toolForm.value = { ...tool };
  isModalOpen.value = true;
};

const saveTool = () => {
  if (!toolForm.value.nome.trim() || !toolForm.value.conteudo.trim()) return;

  const list = [...tools.value];
  if (editingIndex.value >= 0) {
    list[editingIndex.value] = { ...toolForm.value };
  } else {
    list.push({ ...toolForm.value, ordem: list.length });
  }
  tools.value = list;
  isModalOpen.value = false;
};

const removeTool = index => {
  const list = [...tools.value];
  const item = list[index];
  if (item.id) {
    // mark for deletion if persisted
    item._destroy = true;
  } else {
    list.splice(index, 1);
  }
  tools.value = list;
};

const visibleTools = computed(() => {
  return tools.value.filter(t => !t._destroy);
});
</script>

<template>
  <div class="space-y-4 max-w-4xl">
    <div class="bg-n-surface-1 border border-n-weak rounded-xl p-6 space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            Ferramentas & Bases de Conhecimento (Tools)
          </h3>
          <p class="text-xs text-n-slate-11">
            Cada tool representa um bloco de informação específico que a IA
            consulta para responder ao cliente.
          </p>
        </div>
        <Button
          sm
          icon="i-lucide-plus"
          label="Adicionar Tool"
          @click="openAddTool"
        />
      </div>

      <div
        v-if="visibleTools.length === 0"
        class="text-center py-8 border border-dashed border-n-weak rounded-lg text-n-slate-11 text-sm"
      >
        Nenhuma ferramenta cadastrada. Clique em "+ Adicionar Tool" para criar.
      </div>

      <div v-else class="space-y-3">
        <div
          v-for="(tool, index) in visibleTools"
          :key="tool.id || index"
          class="bg-n-surface-2 border border-n-weak rounded-xl p-4 flex items-start justify-between gap-4 hover:border-n-brand/50 transition-colors"
        >
          <div class="space-y-1 flex-1">
            <div class="flex items-center gap-2">
              <span class="font-bold text-sm text-n-slate-12">{{
                tool.nome
              }}</span>
              <span class="text-xs text-n-slate-10 font-mono">ordem: {{ tool.ordem || 0 }}</span>
            </div>
            <p v-if="tool.descricao" class="text-xs text-n-slate-11 italic">
              {{ tool.descricao }}
            </p>
            <p
              class="text-xs text-n-slate-11 line-clamp-2 mt-2 bg-n-surface-1 p-2 rounded border border-n-weak font-mono"
            >
              {{ tool.conteudo }}
            </p>
          </div>
          <div class="flex items-center space-x-1">
            <Button
              ghost
              slate
              sm
              icon="i-lucide-pencil"
              @click="openEditTool(tool, index)"
            />
            <Button
              ghost
              ruby
              sm
              icon="i-lucide-trash-2"
              @click="removeTool(index)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Adicionar / Editar Tool -->
    <div
      v-if="isModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
    >
      <div
        class="bg-n-surface-1 border border-n-weak rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak bg-n-surface-2"
        >
          <h3 class="font-bold text-base text-n-slate-12">
            {{
              editingIndex >= 0
                ? 'Editar Ferramenta (Tool)'
                : 'Nova Ferramenta (Tool)'
            }}
          </h3>
          <Button
            ghost
            slate
            sm
            icon="i-lucide-x"
            @click="isModalOpen = false"
          />
        </div>

        <div class="p-6 space-y-4">
          <div>
            <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
              Nome da Tool <span class="text-red-500">*</span>
            </label>
            <input
              v-model="toolForm.nome"
              type="text"
              placeholder="Ex: Informações da Escola / Tabela de Preços"
              class="reset-base !mb-0 w-full text-sm bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 focus:border-n-brand outline-none"
              required
            />
          </div>

          <div>
            <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
              Descrição (Critério de ativação para a IA)
            </label>
            <input
              v-model="toolForm.descricao"
              type="text"
              placeholder="Ex: Use esta tool quando o lead perguntar sobre valores, matrículas ou estrutura."
              class="reset-base !mb-0 w-full text-sm bg-n-surface-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 focus:border-n-brand outline-none"
            />
          </div>

          <div>
            <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
              Conteúdo da Base de Conhecimento
              <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="toolForm.conteudo"
              rows="8"
              placeholder="Insira o texto com as informações detalhadas que a IA deve consultar..."
              class="reset-base !mb-0 w-full text-sm font-mono bg-n-surface-2 border border-n-weak rounded-lg p-3 text-n-slate-12 focus:border-n-brand outline-none resize-y"
              required
            />
          </div>
        </div>

        <div
          class="flex items-center justify-end gap-2 px-6 py-4 border-t border-n-weak bg-n-surface-2"
        >
          <Button ghost slate label="Cancelar" @click="isModalOpen = false" />
          <Button label="Salvar Tool" @click="saveTool" />
        </div>
      </div>
    </div>
  </div>
</template>
